import type { BlocoHorario, Espelho, EspelhoDia, TipoDia } from "@/api/escala.service";
import { colunasDoMes } from "@/components/escala/espelho";
import { formatarHorarioCurto, horaCurta } from "@/components/escala/quadrosImpressao";

// Toda escalinha tem 31 linhas, para as seis da página terem o mesmo tamanho.
export const LINHAS_ESCALINHA = 31;
export const ESCALINHAS_POR_PAGINA = 6;

const TEXTO_DO_TIPO: Record<string, string> = {
  F: "FOLGA",
  FA: "FOLGA FERIADO",
  C: "COMPENSAÇÃO",
  A: "AFASTADO",
  D: "DESLIGADO",
};

export interface LinhaEscalinha {
  dia: number;
  letra: string;
  // Domingo ou feriado: a linha sai sombreada.
  destaque: boolean;
  feriado: boolean;
  // Vazio antes da admissão.
  tipo: TipoDia;
  // O horário do dia ou a ocorrência por extenso; vazio antes da admissão.
  texto: string;
  // As horas do horário, para alinhar o texto: entrada e saída, com o intervalo no meio quando há.
  horas?: string[];
  // A cor da carga do dia; sem ela, "Padrão".
  cor?: string;
}

// O dia trabalhado mostra o horário aplicado, já com as trocas manuais, ou TRABALHO na escala sem
// carga horária; a folga em feriado cadastrado é FERIADO; o vazio (antes da admissão) fica em branco.
export function textoDoDia(d: EspelhoDia | undefined, feriado: boolean, comCarga = true) {
  if (!d?.tipo) return "";
  if (d.tipo === "T") {
    if (!comCarga) return "TRABALHO";
    return d.horario?.entrada ? formatarHorarioCurto(d.horario) : "SEM HORÁRIO";
  }
  if (d.tipo === "F" && feriado) return "FERIADO";
  return TEXTO_DO_TIPO[d.tipo] ?? d.tipo;
}

const horasDoHorario = (b: BlocoHorario) =>
  (b.intervalo_inicio && b.intervalo_fim ? [b.entrada, b.intervalo_inicio, b.intervalo_fim, b.saida] : [b.entrada, b.saida]).map(horaCurta);

// As 31 linhas de um colaborador; os dias que o mês não tem vêm como null (linha em branco).
export function linhasDaEscalinha(espelho: Espelho, colaboradorId: string): (LinhaEscalinha | null)[] {
  const comCarga = !!espelho.gerada.com_carga_horaria;
  const dias = new Map(espelho.dias.filter((d) => d.colaborador_id === colaboradorId).map((d) => [d.data, d]));
  const cores = new Map((espelho.cargas ?? []).map((c) => [c.id, c.cor]));
  const colunas = colunasDoMes(espelho.gerada.ano, espelho.gerada.mes, espelho.feriados);
  return Array.from({ length: LINHAS_ESCALINHA }, (_, i) => {
    const c = colunas[i];
    if (!c) return null;
    const d = dias.get(c.iso);
    const comHorario = comCarga && d?.tipo === "T" && !!d.horario?.entrada;
    return {
      dia: c.dia,
      letra: c.letra,
      destaque: c.domingo || !!c.feriado,
      feriado: !!c.feriado,
      tipo: d?.tipo ?? "",
      texto: textoDoDia(d, !!c.feriado, comCarga),
      ...(comHorario && {
        horas: horasDoHorario(d.horario!),
        cor: (d.carga_horaria_id && cores.get(d.carga_horaria_id)) || undefined,
      }),
    };
  });
}

// Seis por página, na ordem recebida; a última pode ter menos.
export function paginasDeEscalinhas<T>(itens: T[], porPagina = ESCALINHAS_POR_PAGINA): T[][] {
  const paginas: T[][] = [];
  for (let i = 0; i < itens.length; i += porPagina) paginas.push(itens.slice(i, i + porPagina));
  return paginas;
}
